/**
 * historicalSSOTAdapter.js
 *
 * Phase 3 — Historical Comparison dataset. Cost Code grain ONLY in v1.
 *
 * resource_type AND indirect_category are DEFERRED — see the
 * investigation notes below. Both were implemented and then withdrawn
 * after a Direct/Indirect discriminator investigation proved there is no
 * reliable way to distinguish a Direct Cost Code root from an Indirect
 * one inside forecast_history as currently written.
 *
 * This module does not reconstruct hierarchy itself. It is a thin
 * translation layer over two existing Owners:
 *
 *   1. reconstructHierarchyFromSnapshotRows() — builds the parent_key
 *      tree from raw forecast_history rows. Untouched, called as-is.
 *   2. The already-frozen values on each ROOT node (parent_key === null,
 *      i.e. a Cost Code) — snapshot_budget, snapshot_actual,
 *      snapshot_commitment, eac_amount. These are read directly, never
 *      re-summed, because flattenForSnapshot() froze them as ALREADY
 *      fully rolled up (Resources + Sub Codes) at Commit time — verified
 *      field-by-field against buildCostControlData.js's buildParentRow()
 *      before writing this adapter (see Gate Review below for the
 *      per-measure justification, not just an assumption).
 *
 * resource_type: DEFERRED (confirmed grain incompatibility)
 * ------------------------------------------------------------------------
 * Traced end-to-end: flattenForSnapshot()'s pushRow() (buildCostControlData.js,
 * ~line 1718) passes resourceType as an explicit `null` on the Cost Code
 * root row (~line 1807) — it is ONLY ever non-null on Resource-level rows
 * (~line 1775, inside the `for (const res of parent.resourceRows)` loop).
 * Deliberate, documented design (SS19 in that file's own comments), not a
 * bug. This Adapter's approved v1 grain is Cost Code root only — the
 * field genuinely does not exist there. Widening this Adapter's grain
 * down to Resource level would be a new Phase, not a Step 4 Extension.
 *
 * indirect_category: DEFERRED (discriminator investigation — withdrawn)
 * ------------------------------------------------------------------------
 * indirect_category_id/_name themselves DO exist at Cost Code root grain
 * (computed once per Cost Code from cost_codes.indirect_category_id and
 * passed to every pushRow() call for that Cost Code, including the root's
 * own row — buildCostControlData.js ~line 1770/1807). That part was
 * correctly confirmed compatible.
 *
 * However, actually using indirect_category as a dimension requires
 * separating Direct roots from Indirect roots first (only Indirect roots
 * have a meaningful category; Direct has no category concept at all).
 * The prior implementation attempted this by treating `resource_type ===
 * null` as "this root is Indirect" — but resource_type is ALWAYS null on
 * EVERY Cost Code root (Direct and Indirect alike, per the finding
 * above), so that check excludes nothing and was silently wrong: it let
 * Direct Cost Codes leak into the same 'Uncategorized' bucket as
 * genuinely uncategorized Indirect ones.
 *
 * A second investigation looked for cost_type (the live
 * buildCumulativeData() field that correctly discriminates
 * directRows/indirectRows) as an alternative discriminator.
 * cost_type IS present on every node throughout buildCumulativeData() →
 * buildForecastStructure() → resolveDraft() (carried via `...parent`
 * object spreads at each step) — but flattenForSnapshot()'s pushRow()
 * (buildCostControlData.js, ~line 1718) does not accept it as a
 * parameter and the row object it writes (~line 1725-1766) has no
 * cost_type field at all. It was never wired into the snapshot write
 * path — confirmed by direct inspection, not assumed.
 *
 * Net result: forecast_history currently has NO reliable Direct/Indirect
 * discriminator on the Cost Code root row. Per the explicit instruction
 * this investigation was conducted under, no inference, fallback, or new
 * aggregation may be invented to work around this — indirect_category is
 * deferred alongside resource_type until either (a) cost_type is
 * deliberately added to the snapshot write path (a separate Phase,
 * requiring review of its effect on already-committed historical
 * snapshots), or (b) some other confirmed-reliable discriminator is
 * found. getIndirectCategoryRows()/getResourceTypeRows() and the
 * Structural Aggregation machinery built for them have been removed from
 * this file rather than left in an unreachable/dead state, to avoid any
 * future confusion about whether they are safe to re-enable as-is (they
 * are not, for the reasons above).
 *
 * Explicitly out of scope for v1 (per approved decision):
 *   - package / resource_type / indirect_category / sub_code dimensions
 *   - comparison.type other than "variance"
 *   - any new measure not already defined in Phase 1's registry
 */

import { reconstructHierarchyFromSnapshotRows } from './buildCostControlData'
import { supabase } from './supabase'
import { validateQuery } from './analyticalQuery'

/**
 * Loads raw forecast_history rows for one committed snapshot and
 * reconstructs the hierarchy. Returns only the root (Cost Code level)
 * nodes — Resources and Sub Codes are discarded in v1, per the approved
 * grain restriction. Throws if the hierarchy is structurally invalid,
 * exactly as reconstructHierarchyFromSnapshotRows()'s own contract
 * requires — this adapter does not swallow or paper over that error.
 */
async function loadCostCodeRoots(snapshotId) {
  if (!snapshotId) return []

  const { data, error } = await supabase
    .from('forecast_history')
    .select('row_key, sub_code, cost_code, parent_key, resource_type, eac_amount, etc_amount, ev_amount, snapshot_actual, snapshot_budget, snapshot_commitment, snapshot_accrual, is_forecasted, method')
    .eq('snapshot_id', snapshotId)
  if (error) throw error

  const { roots, errors } = reconstructHierarchyFromSnapshotRows(data || [])

  if (errors.length > 0) {
    // Same posture as buildCostControlDataFromRelease(): a broken
    // parent_key reference is data corruption, not a recoverable
    // business scenario. Never silently proceed with a partial tree —
    // that would produce Cost-Code-level totals that look plausible but
    // are missing children the Commit actually included.
    throw new Error(
      `historicalSSOTAdapter: hierarchy reconstruction failed for ` +
      `snapshot ${snapshotId} — ${errors.length} row(s) reference a ` +
      `parent_key not present in the snapshot: ${JSON.stringify(errors)}`
    )
  }

  // roots here ARE the Cost Code level (parent_key === null by
  // reconstructHierarchyFromSnapshotRows()'s own contract) — no further
  // filtering needed, but asserted explicitly rather than assumed, since
  // a silent wrong assumption here would be exactly the kind of error
  // this whole exercise is trying to prevent.
  return roots.filter(r => r.parent_key == null || r.parent_key === '')
}

// ─── Per-measure mapping off a root (Cost Code) node ───────────────────────
// Every line here is annotated in the Gate Review below with: source field,
// grain, direct-mapping-vs-transformation, and missing-data behavior.
function mapHistoricalMeasure(measureKey, node) {
  switch (measureKey) {
    case 'budget':      return parseFloat(node.snapshot_budget) || 0
    case 'actual':        return parseFloat(node.snapshot_actual) || 0
    case 'eac':             return parseFloat(node.eac_amount) || 0
    case 'commitment':       return parseFloat(node.snapshot_commitment) || 0
    case 'variance':          return (parseFloat(node.snapshot_budget) || 0) - (parseFloat(node.eac_amount) || 0)
    // Not available at the frozen Cost-Code-level snapshot row — see Gate
    // Review. Never fabricated; explicit rejection instead.
    case 'contractValue':
    case 'grossProfit':
    case 'budgetProfit':
      throw new Error(
        `'${measureKey}' is project-level Reference/Derived Metadata, ` +
        `never frozen per-Cost-Code into forecast_history — it is not ` +
        `available from historical_comparison in v1.`
      )
    case 'cpi': {
      // Same formula as everywhere else (EV ÷ Actual), but from the two
      // frozen fields on THIS root node only — not re-derived from
      // children, since ev_amount/snapshot_actual on the root are
      // themselves already the rolled-up figures (see Gate Review).
      const ev = parseFloat(node.ev_amount) || 0
      const actual = parseFloat(node.snapshot_actual) || 0
      return (ev && actual) ? ev / actual : 0
    }
    default:
      throw new Error(`Unmapped historical measure: "${measureKey}"`)
  }
}

/**
 * Executes an AnalyticalQuery against the historical_comparison dataset.
 * dimension MUST be 'cost_code' or null (project-level roll-up is not
 * computed here in v1 — see Gate Review's note on why summing roots
 * would itself require the same additive verification this adapter
 * deliberately avoids doing implicitly).
 */
export async function executeHistoricalQuery(snapshotId, query) {
  const { valid, errors } = validateQuery(query)
  if (!valid) throw new Error(`Invalid query: ${errors.join('; ')}`)
  if (query.dataset !== 'historical_comparison') {
    throw new Error(
      `Dataset "${query.dataset}" is not handled by historicalSSOTAdapter — ` +
      `only "historical_comparison" is wired up here.`
    )
  }
  if (query.dimension && query.dimension !== 'cost_code') {
    throw new Error(
      `historical_comparison supports dimension: 'cost_code' or null only ` +
      `in v1 — "${query.dimension}" (package/resource_type/` +
      `indirect_category/sub_code) is out of scope, per the approved ` +
      `Phase 3 grain restriction and the confirmed Direct/Indirect ` +
      `discriminator gap in forecast_history (see file header).`
    )
  }

  const roots = await loadCostCodeRoots(snapshotId)

  const resultRows = roots.map(node => {
    const values = {}
    for (const m of query.measures) {
      values[m] = mapHistoricalMeasure(m, node)
    }
    if (query.comparison?.type === 'variance') {
      const baseline = mapHistoricalMeasure(query.comparison.baseline, node)
      for (const m of query.measures) {
        if (m === query.comparison.baseline) continue
        values[`${m}_vs_${query.comparison.baseline}`] = values[m] - baseline
      }
    }
    return { key: node.row_key, label: node.cost_code || node.row_key, values }
  })

  return { grain: 'cost_code', snapshotId, rows: resultRows }
}
